import * as RadixCheckbox from '@radix-ui/react-checkbox';
import * as RadixRadio from '@radix-ui/react-radio-group';
import * as RadixSwitch from '@radix-ui/react-switch';
import { Check, Minus } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '../lib/cn';

/*
 * Radix supplies the behaviour — roles, keyboard interaction, indeterminate
 * state, form participation. Reimplementing that correctly is a week nobody
 * budgets and a set of bugs nobody finds until an audit.
 */

export const Checkbox = forwardRef<
  ElementRef<typeof RadixCheckbox.Root>,
  ComponentPropsWithoutRef<typeof RadixCheckbox.Root>
>(function Checkbox({ className, ...props }, ref) {
  return (
    <RadixCheckbox.Root
      ref={ref}
      className={cn(
        'peer size-5 shrink-0 rounded-xs border border-border-strong bg-surface',
        'transition-colors duration-[--hs-duration-fast]',
        'data-[state=checked]:border-accent data-[state=checked]:bg-accent',
        'data-[state=indeterminate]:border-accent data-[state=indeterminate]:bg-accent',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <RadixCheckbox.Indicator className="flex items-center justify-center text-accent-fg">
        {props.checked === 'indeterminate' ? (
          <Minus className="size-3.5" aria-hidden="true" />
        ) : (
          <Check className="size-3.5" aria-hidden="true" />
        )}
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  );
});

export const RadioGroup = forwardRef<
  ElementRef<typeof RadixRadio.Root>,
  ComponentPropsWithoutRef<typeof RadixRadio.Root>
>(function RadioGroup({ className, ...props }, ref) {
  return <RadixRadio.Root ref={ref} className={cn('grid gap-2', className)} {...props} />;
});

export const Radio = forwardRef<
  ElementRef<typeof RadixRadio.Item>,
  ComponentPropsWithoutRef<typeof RadixRadio.Item>
>(function Radio({ className, ...props }, ref) {
  return (
    <RadixRadio.Item
      ref={ref}
      className={cn(
        'size-5 shrink-0 rounded-full border border-border-strong bg-surface',
        'transition-colors duration-[--hs-duration-fast]',
        'data-[state=checked]:border-accent',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <RadixRadio.Indicator className="flex size-full items-center justify-center">
        <span className="size-2.5 rounded-full bg-accent" />
      </RadixRadio.Indicator>
    </RadixRadio.Item>
  );
});

/**
 * A switch is for a setting that applies immediately. A checkbox is for a
 * value that is saved with a form. Using a switch inside a form that has a Save
 * button tells the user their change has already taken effect when it has not.
 */
export const Switch = forwardRef<
  ElementRef<typeof RadixSwitch.Root>,
  ComponentPropsWithoutRef<typeof RadixSwitch.Root>
>(function Switch({ className, ...props }, ref) {
  return (
    <RadixSwitch.Root
      ref={ref}
      className={cn(
        'inline-flex h-6 w-11 shrink-0 items-center rounded-full border-2 border-transparent',
        'bg-border-strong transition-colors duration-[--hs-duration-fast]',
        'data-[state=checked]:bg-accent',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {/* Translated by its inline size so the knob moves the correct way in RTL. */}
      <RadixSwitch.Thumb
        className={cn(
          'pointer-events-none block size-5 rounded-full bg-surface shadow-sm',
          'transition-transform duration-[--hs-duration-fast]',
          'translate-x-0 data-[state=checked]:translate-x-5',
          'rtl:data-[state=checked]:-translate-x-5',
        )}
      />
    </RadixSwitch.Root>
  );
});
